<template>
  <svg
    v-if="pathData"
    :viewBox="viewBox"
    :width="size"
    :height="size"
    class="plot-geo-svg"
    preserveAspectRatio="xMidYMid meet"
  >
    <path
      :d="pathData"
      :fill="fill"
      :stroke="stroke"
      :stroke-width="svgStrokeWidth"
      stroke-linejoin="round"
    />
  </svg>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  geojson: { type: [Object, String], default: null },
  size: { type: [Number, String], default: 48 },
  fill: { type: String, default: 'rgba(76, 175, 80, 0.25)' },
  stroke: { type: String, default: '#2E7D32' },
  strokeWidth: { type: Number, default: 2 }
})

const SVG_SIZE = 100
const PADDING = 8

const parsed = computed(() => {
  if (!props.geojson) return null
  try {
    const g = typeof props.geojson === 'string' ? JSON.parse(props.geojson) : props.geojson
    const geometry = g.type === 'Feature' ? g.geometry
      : g.type === 'FeatureCollection' ? g.features?.[0]?.geometry
      : g
    if (!geometry?.coordinates) return null

    if (geometry.type === 'Polygon') return geometry.coordinates
    if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat()
    return null
  } catch {
    return null
  }
})

const bounds = computed(() => {
  if (!parsed.value) return null
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const ring of parsed.value) {
    for (const [lng, lat] of ring) {
      if (lng < minX) minX = lng
      if (lng > maxX) maxX = lng
      if (lat < minY) minY = lat
      if (lat > maxY) maxY = lat
    }
  }
  return { minX, minY, maxX, maxY, w: maxX - minX || 0.0001, h: maxY - minY || 0.0001 }
})

const viewBox = computed(() => {
  return `${-PADDING} ${-PADDING} ${SVG_SIZE + PADDING * 2} ${SVG_SIZE + PADDING * 2}`
})

// Scale stroke-width relative to SVG coordinate system
const svgStrokeWidth = computed(() => props.strokeWidth)

const pathData = computed(() => {
  if (!parsed.value || !bounds.value) return null
  const { minX, minY, w, h } = bounds.value

  const parts = []
  for (const ring of parsed.value) {
    const points = ring.map(([lng, lat]) => {
      const x = ((lng - minX) / w) * SVG_SIZE
      const y = (1 - (lat - minY) / h) * SVG_SIZE
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    parts.push('M' + points.join('L') + 'Z')
  }
  return parts.join(' ')
})
</script>

<style scoped>
.plot-geo-svg {
  display: block;
  flex-shrink: 0;
}
</style>
